import React, { useState, useEffect } from 'react';
import { adminApi } from '@/services/api';
import { Plus, Pencil, Trash2, Save, X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploadCrop from '@/components/admin/ImageUploadCrop';
import { resolveImage, PROCESS_IMAGE_BASE } from '@/lib/imageUrl';

const AdminProcesses = () => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ nombre: '', descripcion: '', ruta_img: '', orden: 99, activo: true });

  useEffect(() => { loadProcesses(); }, []);

  const loadProcesses = async () => {
    try {
      const res = await adminApi.getProcesses();
      setProcesses(res.data);
    } catch { toast.error('Error al cargar procesos'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ nombre: '', descripcion: '', ruta_img: '', orden: 99, activo: true });
    setEditingId(null);
    setShowCreate(false);
  };

  const startEdit = (process) => {
    setForm({ nombre: process.nombre, descripcion: process.descripcion, ruta_img: process.ruta_img, orden: process.orden, activo: process.activo });
    setEditingId(process.id);
    setShowCreate(false);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingId) {
        await adminApi.updateProcess(editingId, form);
        toast.success('Proceso actualizado');
      } else {
        await adminApi.createProcess(form);
        toast.success('Proceso creado');
      }
      resetForm();
      loadProcesses();
    } catch { toast.error('Error al guardar proceso'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proceso?')) return;
    try {
      await adminApi.deleteProcess(id);
      toast.success('Proceso eliminado');
      loadProcesses();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="p-6" data-testid="admin-processes-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-heading font-bold text-steel-dark uppercase tracking-wider">Procesos</h1>
          <p className="text-steel-gray mt-1">Gestiona los procesos de fabricación</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreate(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="create-process-btn"
        >
          <Plus size={18} /> Nuevo Proceso
        </button>
      </div>

      {/* Create/Edit Form */}
      {(showCreate || editingId) && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6" data-testid="process-form">
          <h3 className="font-bold text-lg text-steel-dark mb-4">
            {editingId ? 'Editar Proceso' : 'Nuevo Proceso'}
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-steel-gray mb-1">Nombre *</label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
                data-testid="process-name-input"
              />
            </div>
            <ImageUploadCrop
              label="Imagen"
              value={form.ruta_img}
              onChange={(path) => setForm({ ...form, ruta_img: path })}
              folder="processes"
              legacyBase={PROCESS_IMAGE_BASE}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-steel-gray mb-1">Descripción</label>
              <textarea
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
                data-testid="process-desc-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-steel-gray mb-1">Orden</label>
              <input
                type="number"
                value={form.orden}
                onChange={(e) => setForm({ ...form, orden: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary"
                data-testid="process-order-input"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  className="w-4 h-4 accent-accent-primary"
                />
                <span className="text-sm text-steel-gray">Activo</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} className="btn-primary flex items-center gap-2" data-testid="process-save-btn">
              <Save size={16} /> {editingId ? 'Actualizar' : 'Crear'}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-steel-gray hover:text-steel-dark transition-colors flex items-center gap-2" data-testid="process-cancel-btn">
              <X size={16} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Processes Table */}
      {loading ? (
        <div className="text-center py-12 text-steel-gray">Cargando...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full" data-testid="processes-table">
            <thead className="bg-steel-dark text-white text-left">
              <tr>
                <th className="px-4 py-3 text-sm font-medium w-12">#</th>
                <th className="px-4 py-3 text-sm font-medium w-20">Imagen</th>
                <th className="px-4 py-3 text-sm font-medium">Nombre</th>
                <th className="px-4 py-3 text-sm font-medium hidden md:table-cell">Descripción</th>
                <th className="px-4 py-3 text-sm font-medium w-20">Estado</th>
                <th className="px-4 py-3 text-sm font-medium w-28">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {processes.map(process => (
                <tr key={process.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-steel-gray">
                    <GripVertical size={14} className="inline mr-1" />{process.orden}
                  </td>
                  <td className="px-4 py-3">
                    <img src={resolveImage(process.ruta_img, PROCESS_IMAGE_BASE) || 'https://placehold.co/56x40/1A1A2E/E63946?text=P'} alt="" className="w-14 h-10 rounded object-cover" onError={(e) => { e.target.src = 'https://placehold.co/56x40/1A1A2E/E63946?text=P'; }} />
                  </td>
                  <td className="px-4 py-3 font-medium text-steel-dark">{process.nombre}</td>
                  <td className="px-4 py-3 text-sm text-steel-gray hidden md:table-cell">
                    {process.descripcion?.substring(0, 80)}{process.descripcion?.length > 80 ? '...' : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${process.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {process.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(process)} className="p-2 text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-process-${process.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(process.id)} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-process-${process.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {processes.length === 0 && (
            <div className="text-center py-12 text-steel-gray">No hay procesos registrados</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminProcesses;
